import {Component, OnInit} from '@angular/core';
import {HttpStatusCode} from '@angular/common/http';
import {SessionStorageService, setToken} from '../../services/session-storage.service';
import {ToastService} from '../../services/toast.service';
import {RestApiError} from '../../services/api/rest-api-error';
import {ActivatedRoute, Router} from '@angular/router';
import {Translations} from '../../services/translations.service';
import {TranslateModule, TranslatePipe, TranslateService} from '@ngx-translate/core';
import {environment} from '../../../environments/environment';
import {AclPermission} from '../../models/acl/acl-group';
import {RestApiService} from '../../services/api/rest-api.service';
import {Routing} from '../../models/routing/routing';
import {RoutingConstants} from '../../models/routing/routing-constants';
import {FormsModule} from '@angular/forms';
import {Dialog} from 'primeng/dialog';
import {Ripple} from 'primeng/ripple';
import {ButtonDirective} from 'primeng/button';
import {InputText} from 'primeng/inputtext';
import {NgIf} from '@angular/common';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

const {params} = RoutingConstants;

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-login',
  templateUrl: './login.component.html',
  imports: [
    FormsModule,
    Dialog,
    Ripple,
    ButtonDirective,
    TranslatePipe,
    TranslateModule,
    InputText,
    NgIf
  ],
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  readonly Routing = Routing;
  readonly RoutingConstants = RoutingConstants;

  username = '';
  password = '';

  twoFactorCode = '';

  formSubmitting = false;
  previousPage?: string;
  insertingTwoFactor = false;
  allowRegistration = environment.allowRegistration;

  constructor(
    public translations: Translations,
    private translateService: TranslateService,
    private api: RestApiService,
    private toastService: ToastService,
    private session: SessionStorageService,
    private router: Router,
    private route: ActivatedRoute) {
  }

  ngOnInit(): void {
    this.previousPage = this.route.snapshot.queryParams[params.previousPage];
    this.route.data.pipe(untilDestroyed(this)).subscribe(data => {
      if (data['mustActivate']) {
        this.toastService.error(
          this.translateService.instant(this.translations.auth.mustActivateTitle),
          this.translateService.instant(this.translations.auth.mustActivate)
        );
      }
    });
    this.route.queryParams.pipe(untilDestroyed(this)).subscribe(queryParams => {
      const token = queryParams[params.token];
      // se ho il token nell'url lo salvo nel local storage e ricarico la pagina rimuovendolo dall'url
      // perché da configurazione delle API in caso di errore 401 la pagina viene ricaricata e ciò comporterebbe
      // ad un loop continuo di refresh
      if (token) {
        setToken(token);
        this._reloadLoginPageWithoutToken();
      } else if (this.session.isAuthenticated) {
        this.autoLogin();
      }
    });
  }

  async login() {
    try {
      this.formSubmitting = true;

      // tenta il login sul nuovo WS
      const response = await this.api.authentication
        .login(this.username, this.password);
      this.session.userData = response.data;
      if (this.session.hasPermission(AclPermission.TwoFactorsAuthentication)) {
        this.insertingTwoFactor = true;
      } else {
        await this._redirectAfterSuccessfulLogin();
      }

    } catch (error) {
      // La login può restituire un errore 401 se l'utente non è autorizzato.
      // Errori di diverso tipo sono lasciati in gestione al chiamante o all'handler generico
      if (error instanceof RestApiError && error.code === HttpStatusCode.Unauthorized) {
        this.toastService.error(
          this.translateService.instant(this.translations.auth.wrongCredentialsTitle),
          this.translateService.instant(this.translations.auth.wrongCredentials)
        );
      } else {
        throw error;
      }
    } finally {
      this.formSubmitting = false;
    }
  }

  async autoLogin() {
    try {
      const response = await this.api.authentication.refreshToken();
      this.session.userData = response.data;

      await this._redirectAfterSuccessfulLogin();
    } catch (error) {
      // autologin fallito, non segnalare
    }
  }

  async _redirectAfterSuccessfulLogin() {
    if (this.previousPage) {
      await this.router.navigateByUrl(this.previousPage);
    } else {
      await this.router.navigate([Routing.dashboard.getBasePath()]);
    }
  }

  async _reloadLoginPageWithoutToken() {
    const filteredParameters = Object.fromEntries(Object.entries(this.route.snapshot.queryParams)
      .filter(([key, value]) => key !== params.token));
    await this.router.navigate([RoutingConstants.login], {
      queryParams: filteredParameters
    });
  }

  async authenticateTwoFactor() {
    if (!this.twoFactorCode || this.twoFactorCode.length < 6) {
      return;
    }
    const response = await this.api.authentication.loginTwoFactor({data: this.twoFactorCode});
    this.session.userData = response.data;
    if (this.session.isAuthenticated) {
      await this._redirectAfterSuccessfulLogin();
    }
  }
}
